//frontend/src/components/Dossier/DossierDetails.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Badge, Button, Alert, Form, Row, Col, Table, Modal, ProgressBar } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { FaFileUpload, FaCheck, FaTimes, FaEye, FaTrash, FaClock, FaExclamationTriangle, FaEdit, FaBriefcase, FaGraduationCap, FaStar } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const ImprovedDossierDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [dossier, setDossier] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadFile, setUploadFile] = useState(null);
    const [docType, setDocType] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [hasPreferences, setHasPreferences] = useState(false);
    const [paymentReceipt, setPaymentReceipt] = useState(null);
    const [uploadingPayment, setUploadingPayment] = useState(false);

    const documentTypes = [
        { value: 'passport', label: 'Passeport', required: true, starred: true },
        { value: 'photo', label: 'Photo d\'identité', required: true, starred: true },
        { value: 'birth_certificate', label: 'Acte de naissance', required: true, starred: true },
        { value: 'cv', label: 'CV', required: true, starred: false },
        { value: 'diploma', label: 'Diplôme', required: true, starred: false },
        { value: 'motivation_letter', label: 'Lettre de motivation', required: true, starred: false },
        { value: 'bank_statement', label: 'Relevé bancaire', required: true, starred: false },
        { value: 'transcript', label: 'Relevé de notes', required: false, starred: false },
        { value: 'work_contract', label: 'Contrat de travail', required: false, starred: false },
        { value: 'job_offer', label: 'Offre d\'emploi', required: false, starred: false },
        { value: 'language_certificate', label: 'Certificat de langue', required: false, starred: false },
        { value: 'marriage_certificate', label: 'Certificat de mariage', required: false, starred: false },
        { value: 'police_certificate', label: 'Casier judiciaire', required: false, starred: false },
        { value: 'medical_certificate', label: 'Certificat médical', required: false, starred: false },
        { value: 'other', label: 'Autre document', required: false, starred: false },
    ].sort((a, b) => {
        if (a.starred && !b.starred) return -1;
        if (!a.starred && b.starred) return 1;
        return 0;
    });

    const checkWorkPreferences = useCallback(async () => {
        try {
            await api.get(`/dossiers/${id}/work-preferences/`);
            setHasPreferences(true);
        } catch (error) {
            // 404 est normal si aucune préférence n'existe
            if (error.response?.status === 404) {
                setHasPreferences(false);
            } else {
                console.error('Error checking work preferences:', error);
            }
        }
    }, [id]);

    const checkStudyPreferences = useCallback(async () => {
        try {
            await api.get(`/dossiers/${id}/study-preferences/`);
            setHasPreferences(true);
        } catch (error) {
            // 404 est normal si aucune préférence n'existe
            if (error.response?.status === 404) {
                setHasPreferences(false);
            } else {
                console.error('Error checking study preferences:', error);
            }
        }
    }, [id]);
    
    const loadDossier = useCallback(async () => {
        try {
            const response = await api.get(`/dossiers/${id}/`);
            setDossier(response.data);
            
            // Vérifier si des préférences existent
            if (response.data.visa_type === 'work') {
                await checkWorkPreferences();
            } else if (response.data.visa_type === 'study') {
                await checkStudyPreferences();
            }
        } catch (error) {
            console.error('Error loading dossier:', error);
            toast.error('Erreur lors du chargement du dossier');
        } finally {
            setLoading(false);
        }
    }, [id, checkWorkPreferences, checkStudyPreferences]);

    const loadDocuments = useCallback(async () => {
        try {
            const response = await api.get(`/dossiers/${id}/documents/`);
            setDocuments(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error loading documents:', error);
        }
    }, [id]);

    useEffect(() => {
        loadDossier();
        loadDocuments();
    }, [loadDossier, loadDocuments]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Le fichier ne doit pas dépasser 10MB');
                return;
            }
            setUploadFile(file);
        }
    };

    const handleFileUpload = async () => {
        if (!uploadFile || !docType) {
            toast.error('Veuillez sélectionner un fichier et un type de document');
            return;
        }

        setUploading(true);

        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('doc_type', docType);

        try {
            await api.post(`/dossiers/${id}/documents/`, formData);
            toast.success('✅ Document téléversé avec succès');
            setShowUploadModal(false);
            setUploadFile(null);
            setDocType('');
            loadDocuments();
        } catch (error) {
            console.error('Error uploading document:', error);
            toast.error('❌ Erreur lors du téléversement');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmitDossier = async () => {
        const requiredDocs = documentTypes.filter(dt => dt.required).map(dt => dt.value);
        const uploadedTypes = documents.map(doc => doc.doc_type);
        const missingDocs = requiredDocs.filter(type => !uploadedTypes.includes(type));

        if (missingDocs.length > 0) {
            const missingLabels = missingDocs.map(type => 
                documentTypes.find(dt => dt.value === type)?.label
            ).join(', ');
            
            toast.error(`Documents manquants : ${missingLabels}`);
            return;
        }

        if (window.confirm('Êtes-vous sûr de vouloir soumettre votre dossier ? Vous ne pourrez plus modifier vos documents après soumission.')) {
            try {
                await api.post(`/dossiers/${id}/submit/`);
                toast.success('✅ Dossier soumis avec succès');
                loadDossier();
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erreur lors de la soumission';
                toast.error(`❌ ${errorMsg}`);
                console.error('Error submitting dossier:', error);
            }
        }
    };

    const handleDeleteDocument = async (docId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
            try {
                await api.delete(`/documents/${docId}`);
                toast.success('Document supprimé');
                loadDocuments();
            } catch (error) {
                console.error('Error deleting document:', error);
                toast.error('Erreur lors de la suppression');
            }
        }
    };

    const handleDeleteDossier = async () => {
        if (window.confirm('⚠️ ATTENTION : Êtes-vous sûr de vouloir supprimer ce dossier ? Cette action est irréversible et supprimera également tous les documents associés.')) {
            try {
                await api.delete(`/dossiers/${id}/`);
                toast.success('Dossier supprimé avec succès');
                navigate('/mes-dossiers');
            } catch (error) {
                console.error('Error deleting dossier:', error);
                if (error.response?.data?.error) {
                    toast.error(`❌ ${error.response.data.error}`);
                } else {
                    toast.error('Erreur lors de la suppression du dossier');
                }
            }
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            draft: 'secondary',
            submitted: 'info',
            in_review: 'warning',
            approved: 'success',
            rejected: 'danger',
        };
        
        const labels = {
            draft: 'Brouillon',
            submitted: 'Soumis',
            in_review: 'En révision',
            approved: 'Approuvé',
            rejected: 'Rejeté',
        };

        return <Badge bg={variants[status]}>{labels[status]}</Badge>;
    };

    const getVisaTypeLabel = (type) => {
        const types = {
            work: 'Visa de Travail',
            study: 'Visa d\'Études',
            tourist: 'Visa Touristique',
            family: 'Regroupement Familial',
            business: 'Visa d\'Affaires',
        };
        return types[type] || type;
    };

    const calculateCompletion = () => {
        const requiredDocs = documentTypes.filter(dt => dt.required);
        const uploadedTypes = documents.map(doc => doc.doc_type);
        const uploaded = requiredDocs.filter(dt => uploadedTypes.includes(dt.value)).length;
        return Math.round((uploaded / requiredDocs.length) * 100);
    };

    const getMissingDocuments = () => {
        const requiredDocs = documentTypes.filter(dt => dt.required);
        const uploadedTypes = documents.map(doc => doc.doc_type);
        return requiredDocs.filter(dt => !uploadedTypes.includes(dt.value)).map(dt => dt.label);
    };

    const handlePreferencesNavigation = () => {
        if (dossier.visa_type === 'work') {
            navigate(`/dossier/${id}/work-preferences`);
        } else if (dossier.visa_type === 'study') {
            navigate(`/dossier/${id}/study-preferences`);
        }
    };

    const handlePaymentReceiptUpload = async () => {
        if (!paymentReceipt) {
            toast.error('Veuillez sélectionner un fichier');
            return;
        }

        if (paymentReceipt.size > 10 * 1024 * 1024) {
            toast.error('Le fichier ne doit pas dépasser 10MB');
            return;
        }

        setUploadingPayment(true);

        const formData = new FormData();
        formData.append('payment_receipt', paymentReceipt);

        try {
            const response = await api.post(`/dossiers/${id}/upload-payment-receipt`, formData);
            toast.success('✅ Reçu de paiement téléversé avec succès');
            setPaymentReceipt(null);
            setDossier(response.data);
        } catch (error) {
            console.error('Error uploading payment receipt:', error);
            toast.error('❌ Erreur lors du téléversement du reçu');
        } finally {
            setUploadingPayment(false);
        }
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <div className="spinner-border text-primary"></div>
                <p className="mt-3">Chargement...</p>
            </Container>
        );
    }

    if (!dossier) {
        return (
            <Container className="py-5">
                <Alert variant="danger">Dossier non trouvé</Alert>
            </Container>
        );
    }

    const completion = calculateCompletion();
    const showPreferencesButton = dossier.visa_type === 'work' || dossier.visa_type === 'study';

    return (
        <Container className="py-4">
            {/* En-tête */}
            <Card className="mb-4 shadow-sm">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col>
                            <h2 className="mb-2">Dossier #{dossier.id}</h2>
                            <div className="d-flex gap-3 align-items-center">
                                {getStatusBadge(dossier.status)}
                                <Badge bg="primary">{getVisaTypeLabel(dossier.visa_type)}</Badge>
                            </div>
                        </Col>
                        <Col xs="auto">
                            <div className="text-end">
                                <small className="text-muted d-block">Créé le</small>
                                <strong>{new Date(dossier.created_at).toLocaleDateString()}</strong>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Bouton Préférences */}
            {showPreferencesButton && (
                <Card className="mb-4">
                    <Card.Header>
                        {dossier.visa_type === 'work' ? <FaBriefcase className="me-2" /> : <FaGraduationCap className="me-2" />}
                        Préférences
                    </Card.Header>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h6>
                                    {hasPreferences ? 'Préférences enregistrées' : 'Préférences non renseignées'}
                                </h6>
                                <p className="text-muted mb-0">
                                    {hasPreferences 
                                        ? (dossier.status === 'draft' 
                                            ? 'Vous pouvez modifier vos préférences' 
                                            : 'Consultez vos préférences enregistrées')
                                        : 'Renseignez vos préférences pour ce type de visa'}
                                </p>
                            </div>
                            <Button
                                variant={hasPreferences ? (dossier.status === 'draft' ? 'warning' : 'info') : 'primary'}
                                onClick={handlePreferencesNavigation}
                            >
                                {hasPreferences ? (
                                    dossier.status === 'draft' ? (
                                        <><FaEdit className="me-2" />Modifier</>
                                    ) : (
                                        <><FaEye className="me-2" />Voir</>
                                    )
                                ) : (
                                    'Renseigner mes préférences'
                                )}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Progression */}
            {dossier.status === 'draft' && (
                <Card className="mb-4">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-bold">Complétion du dossier</span>
                            <span className="fw-bold">{completion}%</span>
                        </div>
                        <ProgressBar
                            now={completion}
                            variant={completion === 100 ? 'success' : 'primary'}
                            style={{ height: '10px' }}
                        />
                        {completion < 100 && (
                            <div className="mt-2">
                                <small className="text-muted">
                                    Documents manquants: {getMissingDocuments().join(', ')}
                                </small>
                            </div>
                        )}
                        {completion < 100 && (
                            <Alert variant="warning" className="mt-3 mb-0">
                                <FaExclamationTriangle className="me-2" />
                                Vous devez téléverser tous les documents obligatoires avant de soumettre votre dossier
                            </Alert>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Commentaire admin */}
            {dossier.admin_comment && (
                <Alert variant={dossier.status === 'rejected' ? 'danger' : 'info'} className="mb-4">
                    <h6>
                        {dossier.status === 'rejected' ? (
                            <><FaTimes className="me-2" />Motif de rejet</>
                        ) : (
                            <><FaClock className="me-2" />Commentaire de l'administrateur</>
                        )}
                    </h6>
                    <p className="mb-0">{dossier.admin_comment}</p>
                </Alert>
            )}

            {/* Documents */}
            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Documents ({documents.length})</h5>
                    {dossier.status === 'draft' && (
                        <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <FaFileUpload className="me-2" />
                            Ajouter un document
                        </Button>
                    )}
                </Card.Header>
                <Card.Body>
                    {documents.length > 0 ? (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Type de document</th>
                                    <th>Date d'ajout</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map((doc) => (
                                    <tr key={doc.id}>
                                        <td>
                                            {documentTypes.find(dt => dt.value === doc.doc_type)?.label || doc.doc_type}
                                        </td>
                                        <td>{new Date(doc.created_at).toLocaleDateString()}</td>
                                        <td>
                                            {doc.verified ? (
                                                <Badge bg="success"><FaCheck className="me-1" />Vérifié</Badge>
                                            ) : (
                                                <Badge bg="secondary"><FaClock className="me-1" />En attente</Badge>
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => {
                                                    const token = localStorage.getItem('access_token');
                                                    window.open(`${api.defaults.baseURL}/documents/${doc.id}?token=${token}`, '_blank');
                                                }}
                                                title="Voir le document"
                                            >
                                                <FaEye className="me-1" />
                                                Voir
                                            </Button>
                                            {dossier.status === 'draft' && (
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    onClick={() => handleDeleteDocument(doc.id)}
                                                    title="Supprimer"
                                                >
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <Alert variant="info" className="mb-0">
                            Aucun document téléversé. Cliquez sur "Ajouter un document" pour commencer.
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Section Paiement */}
            {(dossier.visa_type === 'study' || dossier.visa_type === 'work' || dossier.payment_required) && (
                <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Paiement des frais d'étude</h5>
                        {dossier.payment_completed && (
                            <Badge bg="success">
                                <FaCheck className="me-1" />
                                Payé
                            </Badge>
                        )}
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={8}>
                                <div className="mb-3">
                                    <h6 className="text-primary">Montant à payer : {dossier.payment_amount} CAD</h6>
                                    <p className="text-muted mb-3">
                                        Les frais d'étude du dossier s'élèvent à 125$ canadiens et doivent être payés via notre partenaire sécurisé MoneyFusion.
                                    </p>

                                    <Alert variant="info" className="mb-3">
                                        <strong>🔒 Sécurité et fiabilité garanties</strong><br />
                                        MoneyFusion est une plateforme de paiement sécurisée utilisée par Evisa Canada. Vos données bancaires sont cryptées et protégées selon les normes internationales de sécurité (PCI DSS). Aucun risque de fraude ou de fuite de données.
                                    </Alert>

                                    <div className="d-flex gap-2 mb-3">
                                        <Button
                                            variant="primary"
                                            href="https://my.moneyfusion.net/69629bcd013a07719710b54d"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            💳 Procéder au paiement sécurisé
                                        </Button>
                                        <small className="text-muted align-self-center">
                                            (Ouverture dans une nouvelle fenêtre)
                                        </small>
                                    </div>

                                    <Alert variant="warning">
                                        <strong>⚠️ Important :</strong> Après avoir effectué le paiement, téléversez immédiatement votre reçu de paiement ci-dessous pour validation.
                                    </Alert>
                                </div>
                            </Col>
                            <Col md={4}>
                                <Card className="border-warning">
                                    <Card.Body className="text-center">
                                        <h4 className="text-warning mb-2">125 $</h4>
                                        <small className="text-muted">CAD</small>
                                        <p className="mt-2 mb-0">
                                            <strong>Frais d'étude du dossier</strong>
                                        </p>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Upload du reçu de paiement */}
                        <div className="border-top pt-3">
                            <h6>Téléverser le reçu de paiement</h6>
                            <Form>
                                <Form.Group className="mb-3">
                                    <Form.Label>Fichier du reçu *</Form.Label>
                                    <Form.Control
                                        type="file"
                                        onChange={(e) => setPaymentReceipt(e.target.files[0])}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        disabled={dossier.payment_completed}
                                    />
                                    <Form.Text className="text-muted">
                                        Formats acceptés : PDF, JPG, PNG (Max 10MB)
                                    </Form.Text>
                                </Form.Group>

                                {paymentReceipt && (
                                    <Alert variant="info" className="mb-3">
                                        <strong>Fichier sélectionné :</strong> {paymentReceipt.name}
                                    </Alert>
                                )}

                                {dossier.payment_receipt && (
                                    <Alert variant="success" className="mb-3">
                                        <strong>✅ Reçu déjà téléversé :</strong> {dossier.payment_receipt.split('/').pop()}
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            className="ms-2"
                                            onClick={() => {
                                                const token = localStorage.getItem('access_token');
                                                window.open(`${api.defaults.baseURL}/dossiers/${dossier.id}/payment-receipt?token=${token}`, '_blank');
                                            }}
                                        >
                                            <FaEye className="me-1" />
                                            Voir
                                        </Button>
                                    </Alert>
                                )}

                                <Button
                                    variant="success"
                                    onClick={handlePaymentReceiptUpload}
                                    disabled={!paymentReceipt || uploadingPayment || dossier.payment_completed}
                                >
                                    {uploadingPayment ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Téléversement...
                                        </>
                                    ) : (
                                        <>
                                            <FaFileUpload className="me-2" />
                                            Téléverser le reçu
                                        </>
                                    )}
                                </Button>
                            </Form>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Actions */}
            <div className="d-flex gap-3">
                <Button 
                    variant="secondary" 
                    onClick={() => navigate('/mes-dossiers')}
                >
                    ← Retour à mes dossiers
                </Button>
                
                {dossier.status === 'draft' && (
                    <>
                        {completion === 100 && (
                            <Button 
                                variant="success" 
                                onClick={handleSubmitDossier}
                            >
                                <FaCheck className="me-2" />
                                Soumettre mon dossier
                            </Button>
                        )}
                        
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteDossier}
                        >
                            <FaTrash className="me-2" />
                            Supprimer ce dossier
                        </Button>
                    </>
                )}
            </div>

            {/* Modal Upload */}
            <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Ajouter un document</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Type de document *</Form.Label>
                            <Form.Select
                                value={docType}
                                onChange={(e) => setDocType(e.target.value)}
                                required
                            >
                                <option value="">Sélectionner un type</option>
                                {documentTypes.map((dt, index) => (
                                    <React.Fragment key={dt.value}>
                                        {index > 0 && documentTypes[index - 1].starred && !dt.starred && (
                                            <option disabled>──────────</option>
                                        )}
                                        <option value={dt.value}>
                                            {dt.starred && <FaStar className="text-warning me-1" />}
                                            {dt.label} {dt.required && '*'}
                                        </option>
                                    </React.Fragment>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Fichier *</Form.Label>
                            <Form.Control
                                type="file"
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                            <Form.Text className="text-muted">
                                Formats acceptés : PDF, JPG, PNG (Max 10MB)
                            </Form.Text>
                        </Form.Group>

                        {uploadFile && (
                            <Alert variant="info">
                                <strong>Fichier sélectionné :</strong> {uploadFile.name}
                            </Alert>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
                        Annuler
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleFileUpload}
                        disabled={uploading || !uploadFile || !docType}
                    >
                        {uploading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Téléversement...
                            </>
                        ) : (
                            <>
                                <FaFileUpload className="me-2" />
                                Téléverser
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ImprovedDossierDetails;